import { IDirective, IDirectiveFactory, IScope, IAugmentedJQuery, IAttributes, INgModelController, equals } from "angular";
import Litepicker from "litepicker/dist/nocss/litepicker.umd.js";
import "litepicker/dist/plugins/keyboardnav.js";
import dayjs from "@gtmhub/dayjs";
import { localize } from "@gtmhub/localization";
import { manualStartOfDay, adjustLocalDate, manualEndOfDay } from "./utils";

interface IDaterangepickerScope extends IScope {
  disabled: boolean;
  dateRange: { startDate: string; endDate: string };
  viewDateRange: { startDate: string; endDate: string };

  // Whether to convert dates to local format, e.g. 021-03-11T00:00:00.000Z
  // By default, dates are converted to ISO string in UTC, e.g. 021-03-11T21:00:00.000Z
  local: boolean;
  minDate: string;
  maxDate: string;
}

const viewFormat = "MMM DD, YYYY";

export class DaterangepickerDirective implements IDirective {
  restrict: string;
  templateUrl: string;
  require = "ngModel";
  scope = {
    disabled: "<ngDisabled",
    dateRange: "=ngModel",
    local: "<",
    minDate: "<",
    maxDate: "<",
    mode: "@",
  };

  constructor(private args: { component: boolean }) {
    if (args.component) {
      this.restrict = "E";
      this.templateUrl = require("./daterangepicker.jade");
    } else {
      this.restrict = "A";
    }
  }

  link(scope: IDaterangepickerScope, element: IAugmentedJQuery, attrs: IAttributes, ctrl: INgModelController): void {
    if (attrs.size) {
      element[0].classList.add("size-" + attrs.size);
    }

    const picker = new Litepicker({
      element: this.args.component ? element.find("input")[0] : element[0],
      singleMode: false,
      numberOfMonths: 2,
      numberOfColumns: 2,
      switchingMonths: 1,
      minDate: scope.minDate ? scope.minDate : null,
      maxDate: scope.maxDate ? scope.maxDate : null,
      firstDay: dayjs.localeData().firstDayOfWeek(),
      lang: dayjs.locale(),
      format: {
        parse: (date: Date | string) => dayjs.utc(date).toDate(),
        output: (date: Date) => dayjs.utc(date).toISOString(),
      },
      tooltipText: {
        one: localize("day"),
        other: localize("days"),
      },
      plugins: ["keyboardnav"],
    });
    scope.$on("$destroy", () => picker.destroy());

    scope.$watch("dateRange", () => {
      if (scope.dateRange) {
        const startDate = scope.local ? manualStartOfDay(scope.dateRange.startDate) : dayjs(scope.dateRange.startDate).startOf("day").toISOString();
        const endDate = scope.local ? manualStartOfDay(scope.dateRange.endDate) : dayjs(scope.dateRange.endDate).startOf("day").toISOString();
        picker.setDateRange(startDate, endDate);
        scope.viewDateRange = { startDate: dayjs(startDate).format(viewFormat), endDate: dayjs(endDate).format(viewFormat) };
      } else {
        delete scope.viewDateRange;
        picker.clearSelection();
      }
    });

    const onSelected = () => {
      const rawStartDate = dayjs(picker.getStartDate().toJSDate());
      const rawEndDate = dayjs(picker.getEndDate().toJSDate());

      const startDate = scope.local ? manualStartOfDay(adjustLocalDate(rawStartDate).toISOString()) : rawStartDate.startOf("day").toISOString();
      const endDate = scope.local ? manualEndOfDay(adjustLocalDate(rawEndDate).toISOString()) : rawEndDate.endOf("day").toISOString();

      const dateRange = { startDate, endDate };
      if (!equals(dateRange, scope.dateRange)) {
        picker.off("selected", onSelected);
        ctrl.$setViewValue({ startDate, endDate });
      }
    };

    picker.on("show", () => {
      picker.on("selected", onSelected);
    });

    picker.on("hide", () => {
      picker.off("selected", onSelected);
    });

    picker.on("render", (ui: Element) => {
      ui.classList.add("gh-daterangepicker");
    });

    // Allow all icons (calendar, down arrow) to show the picker
    document.querySelectorAll(".daterangepicker-toggle").forEach((toggle) => toggle.addEventListener("click", () => picker.show()));
  }

  static factory(args: { component: boolean }): IDirectiveFactory {
    return () => new DaterangepickerDirective(args);
  }
}
